import { useState, useEffect, useRef } from "react";
import Logo from "../../assets/logo.jpeg";

const colors = {
  green: "#2d6a4f",
  greenMid: "#40916c",
  greenLight: "#74c69d",
  greenPale: "#d8f3dc",
  greenBg: "#f0faf3",
  gold: "#b5881d",
  goldLight: "#f9e4a0",
  goldPale: "#fdf6dc",
  ink: "#1a2e1e",
  ink2: "#3d5a44",
  ink3: "#6b8c72",
  border: "#c8e6c9",
};

const sections = [
  { id: "s1",  num: "01", title: "Overview" },
  { id: "s2",  num: "02", title: "Data We Collect" },
  { id: "s3",  num: "03", title: "How We Use It" },
  { id: "s4",  num: "04", title: "Data Sharing" },
  { id: "s5",  num: "05", title: "Cookies" },
  { id: "s6",  num: "06", title: "Data Retention" },
  { id: "s7",  num: "07", title: "Security" },
  { id: "s8",  num: "08", title: "Your Rights" },
  { id: "s9",  num: "09", title: "Children's Privacy" },
  { id: "s10", num: "10", title: "Policy Changes" },
  { id: "s11", num: "11", title: "Contact Us" },
];

const dataCards = [
  { icon: "👤", title: "Identity Data",  desc: "Full name, email address, phone number, and account credentials used to register on Tamigo." },
  { icon: "🛒", title: "Order Data",     desc: "Order history, delivery addresses, preferred pickup locations, and transaction records." },
  { icon: "📱", title: "Usage Data",     desc: "Log files, IP address, browser type, device identifiers, pages visited, clicks, and session duration." },
  { icon: "📍", title: "Location Data",  desc: "Delivery address and approximate location to facilitate home delivery and nearby store services." },
];

const rights = [
  "✅ Access your data", "✏️ Correct inaccuracies", "🗑️ Request deletion",
  "📦 Data portability", "⛔ Object to processing", "↩️ Withdraw consent", "📣 Lodge a complaint",
];

function useWindowWidth() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return w;
}

function BulletList({ items }) {
  return (
    <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 8, margin: "8px 0", padding: 0 }}>
      {items.map((item, i) => (
        <li key={i} style={{ display: "flex", gap: 10, fontSize: 14, color: colors.ink2, lineHeight: 1.7 }}>
          <span style={{ width: 7, height: 7, minWidth: 7, borderRadius: "50%", background: colors.greenMid, marginTop: 8, display: "inline-block" }} />
          <span dangerouslySetInnerHTML={{ __html: item }} />
        </li>
      ))}
    </ul>
  );
}

function HighlightBox({ children }) {
  return (
    <div style={{ background: colors.goldPale, borderRadius: 12, padding: "12px 16px", margin: "12px 0", border: "1px solid rgba(181,136,29,0.2)" }}>
      <p style={{ fontSize: 13.5, color: "#7a5a10", margin: 0, lineHeight: 1.7 }}>{children}</p>
    </div>
  );
}

function SectionWrapper({ id, num, title, children, isMobile }) {
  return (
    <div id={id} style={{ marginBottom: "2.5rem", paddingBottom: "2.5rem", borderBottom: `1px solid ${colors.border}` }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: "1rem" }}>
        <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 13, color: colors.gold, width: 26, flexShrink: 0 }}>{num}</span>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: isMobile ? "1.05rem" : "1.2rem", fontWeight: 600, color: colors.ink, letterSpacing: "-0.01em", margin: 0 }}>{title}</h2>
      </div>
      <div style={{ paddingLeft: isMobile ? 0 : 38 }}>{children}</div>
    </div>
  );
}

function Para({ children, style = {} }) {
  return <p style={{ fontSize: 14, color: colors.ink2, lineHeight: 1.8, margin: "0 0 0.85rem", ...style }}>{children}</p>;
}

export default function MilliyoPrivacyPolicy() {
  const [activeId, setActiveId] = useState("s1");
  const [tocOpen, setTocOpen]   = useState(false);
  const contentRef              = useRef(null);
  const width                   = useWindowWidth();
  const isMobile                = width < 768;
  const isTablet                = width >= 768 && width < 1024;
  const headerH                 = isMobile ? 90 : 105;

  useEffect(() => {
    if (!document.getElementById("milliyo-fonts")) {
      const link  = document.createElement("link");
      link.id     = "milliyo-fonts";
      link.rel    = "stylesheet";
      link.href   = "https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600&family=Playfair+Display:ital,wght@0,400;0,600;1,400&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    const els = sections.map((s) => document.getElementById(s.id)).filter(Boolean);
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActiveId(e.target.id); }),
      { rootMargin: "-20% 0px -70% 0px" }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const NavList = () => (
    <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 2, padding: 0, margin: 0 }}>
      {sections.map((s) => (
        <li key={s.id}>
          <a
            href={`#${s.id}`}
            onClick={(e) => {
              e.preventDefault();
              document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth" });
              setTocOpen(false);
            }}
            style={{
              display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
              fontSize: 13, color: activeId === s.id ? colors.green : colors.ink3,
              textDecoration: "none", borderRadius: 8,
              borderLeft: `2px solid ${activeId === s.id ? colors.green : "transparent"}`,
              background: activeId === s.id ? colors.greenPale : "transparent",
              fontWeight: activeId === s.id ? 500 : 400, transition: "all 0.18s",
            }}
          >
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 12, color: colors.gold, minWidth: 18 }}>{s.num}</span>
            {s.title}
          </a>
        </li>
      ))}
    </ul>
  );

  return (
    <div style={{ fontFamily: "'Poppins', sans-serif", background: "#f7fdf8", color: colors.ink, minHeight: "100vh", fontSize: 15, lineHeight: 1.75 }}>

      {/* HEADER */}
      <header style={{
        background: "#fff", borderBottom: `2px solid ${colors.greenPale}`,
        padding: isMobile ? "0 1rem" : "0 clamp(1.5rem,5vw,4rem)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        height: headerH, position: "sticky", top: 0, zIndex: 200,
        boxShadow: "0 2px 12px rgba(45,106,79,0.08)",
      }}>
        <img src={Logo} alt="Tamigo" style={{ height: isMobile ? 80 : 105, width: "auto", objectFit: "contain" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {!isMobile && (
            <span style={{
              background: colors.greenPale, color: colors.green, fontSize: 11, fontWeight: 500,
              letterSpacing: "0.07em", textTransform: "uppercase", padding: "5px 14px",
              borderRadius: 100, border: `1px solid ${colors.border}`,
            }}>Privacy Policy</span>
          )}
          {isMobile && (
            <button
              onClick={() => setTocOpen((o) => !o)}
              style={{
                background: colors.greenPale, border: `1px solid ${colors.border}`, borderRadius: 8,
                padding: "6px 12px", fontSize: 12, color: colors.green, fontWeight: 500,
                cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
              }}
            >☰ Contents</button>
          )}
        </div>
      </header>

      {/* MOBILE DRAWER */}
      {isMobile && tocOpen && (
        <div
          style={{ position: "fixed", top: headerH, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.4)", zIndex: 150 }}
          onClick={() => setTocOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", width: "78%", maxWidth: 280, height: "100%", padding: "1.5rem 1rem", overflowY: "auto", boxShadow: "4px 0 24px rgba(0,0,0,0.12)" }}
          >
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: colors.ink3, marginBottom: 14, paddingLeft: 10 }}>Contents</div>
            <NavList />
          </div>
        </div>
      )}

      {/* HERO */}
      <div style={{
        background: `linear-gradient(135deg, ${colors.green} 0%, ${colors.greenMid} 55%, #52b788 100%)`,
        padding: isMobile ? "2rem 1rem" : "clamp(2.5rem,6vw,4.5rem) clamp(1.5rem,6vw,4rem)",
        position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", right: -60, top: -60, width: 280, height: 280, borderRadius: "50%", background: "rgba(255,255,255,0.06)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", left: -40, bottom: -80, width: 200, height: 200, borderRadius: "50%", background: "rgba(181,136,29,0.15)", pointerEvents: "none" }} />
        <div style={{
          position: "relative", zIndex: 1,
          display: "flex", flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "flex-start" : "flex-end",
          justifyContent: "space-between", gap: "1.25rem",
        }}>
          <div>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: "rgba(255,255,255,0.18)", color: "#fff", fontSize: 11, fontWeight: 500,
              letterSpacing: "0.1em", textTransform: "uppercase", padding: "5px 14px",
              borderRadius: 100, border: "1px solid rgba(255,255,255,0.3)", marginBottom: "1rem",
            }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: colors.goldLight, display: "inline-block" }} />
              Legal Document
            </div>
            <h1 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: isMobile ? "1.7rem" : "clamp(1.9rem,4.5vw,3rem)",
              fontWeight: 600, color: "#fff", lineHeight: 1.2, letterSpacing: "-0.02em", margin: 0,
            }}>
              Your privacy is<br />
              <em style={{ fontStyle: "italic", color: colors.goldLight }}>our priority.</em>
            </h1>
          </div>
          <div style={{ textAlign: isMobile ? "left" : "right", flexShrink: 0 }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em", color: "rgba(255,255,255,0.6)", marginBottom: 4 }}>Effective date</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 17, color: "#fff" }}>May 28, 2026</div>
          </div>
        </div>
      </div>

      {/* BODY GRID */}
      <div style={{
        maxWidth: 1080, margin: "0 auto",
        padding: isMobile ? "0 1rem" : "0 clamp(1rem,4vw,2.5rem)",
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : isTablet ? "180px 1fr" : "220px 1fr",
      }}>

        {/* DESKTOP SIDEBAR */}
        {!isMobile && (
          <nav style={{
            padding: "2rem 1.5rem 2rem 0",
            borderRight: `1.5px solid ${colors.border}`,
            position: "sticky", top: headerH,
            height: `calc(100vh - ${headerH}px)`,
            overflowY: "auto", scrollbarWidth: "thin",
          }}>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: colors.ink3, marginBottom: 12, paddingLeft: 10 }}>Contents</div>
            <NavList />
          </nav>
        )}

        {/* MAIN */}
        <main ref={contentRef} style={{ padding: isMobile ? "1.75rem 0 3rem" : "2.5rem 0 4rem 2.5rem", minWidth: 0 }}>

          <div style={{
            background: colors.greenBg, borderLeft: `3px solid ${colors.greenMid}`,
            borderRadius: "0 12px 12px 0", padding: "14px 18px", marginBottom: "2.25rem",
            fontSize: 14, color: colors.green, lineHeight: 1.7,
          }}>
            <strong>Tamigo</strong> is committed to protecting your personal information. This Privacy Policy explains what data we collect, how we use it, and the choices you have. By using our platform, you agree to the practices described here.
          </div>

          <SectionWrapper id="s1" num="01" title="Overview" isMobile={isMobile}>
            <Para>Tamigo ("we," "us," or "our") operates a platform that provides home delivery and in-store pickup services. This Privacy Policy applies to all users of our website, mobile application, and related services (Tamigo.zentarq.com).</Para>
            <Para>We operate in compliance with applicable data protection laws, including the Information Technology Act, 2000 (India). Our registered office is in India.</Para>
          </SectionWrapper>

          <SectionWrapper id="s2" num="02" title="Data We Collect" isMobile={isMobile}>
            <Para>We collect information necessary to provide and improve our services across three main categories:</Para>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10, margin: "12px 0" }}>
              {dataCards.map((c, i) => (
                <div key={i}
                  style={{ background: "#fff", border: `1px solid ${colors.border}`, borderRadius: 12, padding: "14px 16px", transition: "box-shadow 0.15s" }}
                  onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 16px rgba(45,106,79,0.1)")}
                  onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
                >
                  <div style={{ fontSize: 20, marginBottom: 5 }}>{c.icon}</div>
                  <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: colors.greenMid, marginBottom: 3 }}>{c.title}</div>
                  <div style={{ fontSize: 12.5, color: colors.ink3, lineHeight: 1.55 }}>{c.desc}</div>
                </div>
              ))}
            </div>
          </SectionWrapper>

          <SectionWrapper id="s3" num="03" title="How We Use Your Information" isMobile={isMobile}>
            <Para>Your data powers the Tamigo experience. We use it to:</Para>
            <BulletList items={[
              "Create and manage your Tamigo account and verify your identity",
              "Process and fulfil your orders — both home delivery and on-store pickup",
              "Send order confirmations, updates, and delivery notifications",
              "Provide personalised product recommendations and offers",
              "Manage delivery operations and delivery partner assignments",
              "Detect fraud, unauthorised access, and security threats",
              "Improve our platform through analytics and feedback",
              "Send promotional communications (you may opt out at any time)",
              "Meet legal, accounting, or reporting requirements",
            ]} />
            <HighlightBox>We never sell your personal data to third parties for their own marketing purposes.</HighlightBox>
          </SectionWrapper>

          <SectionWrapper id="s4" num="04" title="Data Sharing & Disclosure" isMobile={isMobile}>
            <Para>We share your information only in the following limited circumstances:</Para>
            <BulletList items={[
              "<strong>Delivery Partners:</strong> Your name, address, and order details are shared with delivery personnel to complete your order.",
              "<strong>Service Providers:</strong> Trusted partners (payment processors, cloud hosting, SMS/email vendors) who process data under strict agreements.",
              "<strong>Legal Requirements:</strong> When required by law, court order, or regulatory authority.",
              "<strong>Business Transfers:</strong> In connection with a merger, acquisition, or asset sale, with prior notice to you.",
              "<strong>With Your Consent:</strong> For any other purpose, only with your explicit consent.",
            ]} />
          </SectionWrapper>

          <SectionWrapper id="s5" num="05" title="Cookies & Tracking" isMobile={isMobile}>
            <Para>Our platform uses cookies and similar technologies to maintain sessions, remember preferences, and analyse usage patterns:</Para>
            <BulletList items={[
              "<strong>Essential Cookies:</strong> Required for core functionality such as login sessions and cart management. Cannot be disabled.",
              "<strong>Analytics Cookies:</strong> Help us understand how users interact with Tamigo. Can be opted out via browser settings.",
              "<strong>Preference Cookies:</strong> Remember your delivery preferences and personalisation choices.",
              "<strong>Marketing Cookies:</strong> Used only with your consent to serve relevant product content.",
            ]} />
            <Para>You can manage cookie preferences through your browser settings at any time.</Para>
          </SectionWrapper>

          <SectionWrapper id="s6" num="06" title="Data Retention" isMobile={isMobile}>
            <Para>We retain your personal data only as long as necessary for the purposes set out in this policy or as required by law. Order and transaction records may be retained for up to 7 years in accordance with Indian tax and accounting regulations.</Para>
            <Para>After account deletion, most personal data is purged within 90 days, except where legal retention obligations apply. Anonymised, aggregated data may be retained indefinitely for analytics.</Para>
          </SectionWrapper>

          <SectionWrapper id="s7" num="07" title="Data Security" isMobile={isMobile}>
            <Para>We implement industry-standard safeguards to protect your data:</Para>
            <BulletList items={[
              "SSL/TLS encryption for all data transmitted between your device and our servers",
              "Encrypted storage of sensitive account and payment information",
              "Strict role-based access controls — only authorised staff can access user data",
              "Regular security audits and vulnerability assessments",
              "Secure admin panel with authentication controls",
            ]} />
            <HighlightBox>If you believe your account has been compromised, contact us immediately at security@Tamigo.in.</HighlightBox>
          </SectionWrapper>

          <SectionWrapper id="s8" num="08" title="Your Rights" isMobile={isMobile}>
            <Para>You have meaningful control over your personal data. Your rights include:</Para>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7, margin: "12px 0" }}>
              {rights.map((r, i) => (
                <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: colors.greenPale, color: colors.green, fontSize: 12.5, fontWeight: 500, padding: "5px 13px", borderRadius: 100, border: `1px solid ${colors.border}` }}>{r}</span>
              ))}
            </div>
            <Para>To exercise any right, email <strong>privacy@Tamigo.in</strong>. We will respond within 30 days.</Para>
          </SectionWrapper>

          <SectionWrapper id="s9" num="09" title="Children's Privacy" isMobile={isMobile}>
            <Para>Tamigo's services are intended for individuals aged 18 and above. We do not knowingly collect personal data from minors. If we become aware that a child under 18 has provided us with personal information, we will delete it promptly.</Para>
            <Para>If you are a parent or guardian and believe your child has submitted information to us, please contact us at privacy@Tamigo.in.</Para>
          </SectionWrapper>

          <SectionWrapper id="s10" num="10" title="Policy Changes" isMobile={isMobile}>
            <Para>We may update this Privacy Policy periodically to reflect changes in our practices, technology, or legal requirements. When we make material changes, we will notify you via email or a prominent in-app notification at least 14 days before the changes take effect.</Para>
            <Para>Your continued use of Tamigo after any update constitutes acceptance of the revised policy.</Para>
          </SectionWrapper>

          <SectionWrapper id="s11" num="11" title="Contact Us" isMobile={isMobile}>
            <Para>Questions, concerns, or requests about this Privacy Policy? Reach our team at:</Para>
            <div style={{
              background: colors.green, borderRadius: 14,
              padding: isMobile ? "1.25rem" : "1.75rem",
              display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: isMobile ? "1rem" : "1.5rem", alignItems: "center", marginTop: 12,
            }}>
              <div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.05rem", fontWeight: 400, color: "#fff", margin: "0 0 6px", lineHeight: 1.4 }}>
                  Tamigo Support &amp;<br />Privacy Team
                </h3>
                <p style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", margin: 0 }}>We aim to respond within 30 business days.</p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  { label: "Privacy Enquiries", value: "privacy@Tamigo.in" },
                  { label: "Security Issues",   value: "security@Tamigo.in" },
                  { label: "Platform",          value: "Tamigo.zentarq.com" },
                ].map((item) => (
                  <div key={item.label} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(255,255,255,0.45)" }}>{item.label}</span>
                    <span style={{ fontSize: 13, color: colors.goldLight }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </SectionWrapper>

        </main>
      </div>

      {/* FOOTER */}
      <footer style={{
        background: colors.green,
        padding: isMobile ? "1.25rem 1rem" : "1.5rem clamp(1.5rem,5vw,4rem)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: "0.75rem",
      }}>
        <span style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: 17, color: "rgba(255,255,255,0.85)" }}>
          Milli<span style={{ color: colors.goldLight }}>yo</span>
        </span>
        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
          © 2026 Tamigo · Privacy Policy · Effective May 28, 2026
        </span>
      </footer>

    </div>
  );
}